import React, { useEffect, useRef, useState } from 'react';

import {
  Button,
  DatePicker,
  DateRangePicker,
  Divider,
  ExcelIcon,
  Form,
  Input,
  Pagination,
  RefreshIcon,
  Select,
  Text,
  TextButton,
  type TypeDatePickerRef,
  type TypeSelectOption,
  dialogService,
  toastService,
} from '@thefarmersfront/kitchen';

import type { IRowNode } from 'ag-grid-community';

import { PageTitle, Table, TableTopField } from '~/components/common';
import { PageWrapper } from '~/components/layout';
import {
  OrderDetailModal,
  OrderExcelDownloadModal,
  OrderExcelUploadFailModal,
  OrderExcelUploadModal,
} from '~/components/order';

import { useDownloadReady, useModal, useToggle } from '~/hooks/common';
import {
  useGetCodes,
  useGetOrderDefaultExcel,
  useGetOrderOutboundOrderCode,
  useGetOrders,
  useGetOrdersInvoiceNumberExcel,
  useGetOrdersOrderExcel,
  usePostOrderCancel,
} from '~/hooks/order';
import { useLoadingStore, useUserInfoStore } from '~/store';
import {
  formatDate,
  formatMinute,
  formatTime,
  getDateByOffset,
  getDateRangeInfo,
  validateAlphabetNumericOnly,
} from '~/utils';
import { getOrderOrdersColumnDefs } from '~/utils/order';

import { BREAD_CRUMB, OPTION_ALL, ORDERS, PATH, TOAST_MESSAGE } from '~/constants';
import {
  INITIAL_ORDER_PAGE_OFFSET,
  INITIAL_ORDER_PAGE_SIZE,
  KEYWORD_PLACE_HOLDER,
  KEYWORD_TYPE,
  MAX_KEYWORD_COUNT,
  ORDER_PAGE_SIZE,
  ORDER_UPLOAD_RESTRICTED_HOUR_END,
  ORDER_UPLOAD_RESTRICTED_HOUR_START,
  OUTBOUND_STATUS,
  PERIOD_TYPE,
  type TypeOrderPageSize,
} from '~/constants/order';

import type { TypeErrorResponseData } from '~/types/common.types';
import type {
  TypeGetOrdersParams,
  TypeOrderFilter,
  TypeOrderFilterState,
  TypeOrderOrderCode,
  TypeOrders,
  TypePostOrderExcelResponse,
} from '~/types/order.types';

import styles from './OrderOrdersPage.module.scss';

const INITIAL_ORDER_FILTER: TypeOrderFilter = {
  keywordType: null,
  keyword: '',
  periodType: null,
  deliveryStartDate: getDateByOffset(1, 'day'),
  deliveryEndDate: getDateByOffset(1, 'day'),
  paymentStartAt: null,
  paymentStartAtTime: '',
  paymentStartAtMinute: '',
  paymentEndAt: null,
  paymentEndAtTime: '',
  paymentEndAtMinute: '',
  outboundStatus: OPTION_ALL,
  deliveryPolicy: OPTION_ALL,
  courierCode: OPTION_ALL,
  clusterCenter: OPTION_ALL,
  pageSize: INITIAL_ORDER_PAGE_SIZE,
  currentPage: INITIAL_ORDER_PAGE_OFFSET,
};

/**
 * 출고 주문 관리 > 출고 주문 조회 페이지
 */
const OrderOrdersPage = () => {
  const { setIsLoading } = useLoadingStore();

  const hasPermission = useUserInfoStore((state) => state.hasPermission);
  const hasPermission3PL = hasPermission([{ ownerType: '3PL' }]);
  const hasPermissionNFAFulfillment = hasPermission([{ ownerType: 'NFA', logisticsTypes: ['FULFILLMENT'] }]);

  const [ordersData, setOrdersData] = useState<TypeOrders[] | null>(null);
  const [totalCount, setTotalCount] = useState<number | null>(null);

  const [searchedParams, setSearchedParams] = useState<TypeGetOrdersParams | null>(null);

  const [orderDetailsData, setOrderDetailsData] = useState<TypeOrderOrderCode | null>(null);

  const [excelUploadFailData, setExcelUploadFailData] = useState<TypePostOrderExcelResponse | null>(null);

  const [orderFilter, setOrderFilter] = useState<TypeOrderFilter>(INITIAL_ORDER_FILTER);

  const [orderFilterState, setOrderFilterState] = useState<TypeOrderFilterState>({
    keyword: 'default',
    deliveryDate: 'default',
    paymentAt: 'default',
  });

  const [optionKeywordType, setOptionKeywordType] = useState<TypeSelectOption<string>[]>([]);
  const [optionPeriodType, setOptionPeriodType] = useState<TypeSelectOption<string>[]>([]);
  const [optionOutboundStatus, setOptionOutboundStatus] = useState<TypeSelectOption<string>[]>([]);
  const [optionDeliveryPolicy, setOptionDeliveryPolicy] = useState<TypeSelectOption<string>[]>([]);
  const [optionCourierCode, setOptionCourierCode] = useState<TypeSelectOption<string>[]>([]);
  const [optionClusterCenter, setOptionClusterCenter] = useState<TypeSelectOption<string>[]>([]);

  const [selectedRows, setSelectedRows] = useState<IRowNode<TypeOrders>[]>([]);

  const paymentEndAtDateRef = useRef<TypeDatePickerRef>(null);

  const pageOptions = Object.values(ORDER_PAGE_SIZE);

  const { requestCodes } = useGetCodes();
  const { requestOrderList } = useGetOrders();
  const { requestOrderDetail } = useGetOrderOutboundOrderCode();
  const { getOrderDefaultExcel } = useGetOrderDefaultExcel();
  const { postOrderCancel } = usePostOrderCancel();
  const { getOrdersOrderExcel } = useGetOrdersOrderExcel();
  const { getOrdersInvoiceNumberExcel } = useGetOrdersInvoiceNumberExcel();

  const { isToggle: isFilterOpen, handleToggle: handleFilterToggle } = useToggle({ initialState: true });

  const { isDownloadReady, markDownloadReady, checkFilterChange } = useDownloadReady({
    initialFilter: INITIAL_ORDER_FILTER,
  });

  const {
    isOpen: isOrderDetailModalOpen,
    handleOpenModal: handleOpenOrderDetailModal,
    handleCloseModal: handleCloseOrderDetailModal,
  } = useModal({
    onOpen: async (outboundOrderCode?: string) => {
      if (!outboundOrderCode) return;

      requestOrderDetail(
        { outboundOrderCode },
        {
          onSuccess: (data) => setOrderDetailsData(data),
          onError: (error) => {
            toastService.addToast({
              type: 'error',
              message: error?.message || '출고 주문 상세 내용을 호출하는데 실패했습니다.\n다시 시도해주세요.',
            });
          },
        }
      );
    },
  });

  const {
    isOpen: isOrderExcelUploadModalOpen,
    handleOpenModal: handleOpenOrderExcelUploadModal,
    handleCloseModal: handleCancelOrderExcelUploadModal,
    handleConfirmModal: handleConfirmOrderExcelUploadModal,
  } = useModal({
    onConfirm: (data?: TypePostOrderExcelResponse) => {
      if (data) {
        handleOrderExcelUploadResult(data);
      }
    },
  });

  const {
    isOpen: isOrderExcelUploadFailModalOpen,
    handleOpenModal: handleOpenOrderExcelUploadFailModal,
    handleCloseModal: handleCloseOrderExcelUploadFailModal,
  } = useModal({
    onCloseCompleted: () =>
      fetchOrdersData({
        ...orderFilter,
        currentPage: INITIAL_ORDER_PAGE_OFFSET,
      }),
  });

  const {
    isOpen: isOrderExcelDownloadModalOpen,
    handleOpenModal: handleOpenOrderExcelDownloadModal,
    handleCloseModal: handleCloseOrderExcelDownloadModal,
    handleConfirmModal: handleConfirmOrderExcelDownloadModal,
  } = useModal({
    onConfirm: (reason?: string) => {
      if (reason) {
        fetchOrdersOrderExcelDownload(reason);
      }
    },
  });

  const {
    isOpen: isInvoiceExcelDownloadModalOpen,
    handleOpenModal: handleOpenInvoiceExcelDownloadModal,
    handleCloseModal: handleCloseInvoiceExcelDownloadModal,
    handleConfirmModal: handleConfirmInvoiceExcelDownloadModal,
  } = useModal({
    onConfirm: (reason?: string) => {
      if (reason) {
        fetchOrdersInvoiceNumberExcelDownload(reason);
      }
    },
  });

  const { columnDefs } = getOrderOrdersColumnDefs({
    hasPermission3PL,
    onClickOutboundCode: handleOpenOrderDetailModal,
  });

  useEffect(() => {
    requestCodes(
      {
        codeTypes: [
          'order-search-keyword',
          'order-search-date-type',
          'outbound-status',
          'delivery-policy',
          'courier-code',
          'cluster-center',
        ],
      },
      {
        onSuccess: (codes) => {
          setOrderFilter({
            ...orderFilter,
            keywordType: codes.orderSearchKeyword[0] ?? null,
            periodType: codes.orderSearchDateType[0] ?? null,
          });

          setOptionKeywordType(codes.orderSearchKeyword);
          setOptionPeriodType(codes.orderSearchDateType);
          setOptionOutboundStatus([OPTION_ALL, ...codes.outboundStatus]);
          setOptionDeliveryPolicy([OPTION_ALL, ...codes.deliveryPolicy]);
          setOptionCourierCode([OPTION_ALL, ...codes.courierCode]);
          setOptionClusterCenter([OPTION_ALL, ...codes.clusterCenter]);
        },
      }
    );
  }, []);

  useEffect(() => {
    paymentEndAtDateRef.current?.focus();
  }, [orderFilter.paymentStartAt]);

  useEffect(() => {
    checkFilterChange(orderFilter);
  }, [orderFilter]);

  const fetchOrdersData = async (orderFilterParams: TypeOrderFilter = orderFilter) => {
    const {
      currentPage,
      pageSize,
      keywordType,
      keyword,
      periodType,
      deliveryPolicy,
      courierCode,
      clusterCenter,
      outboundStatus,
      deliveryStartDate,
      deliveryEndDate,
      paymentStartAt,
      paymentStartAtTime,
      paymentStartAtMinute,
      paymentEndAt,
      paymentEndAtTime,
      paymentEndAtMinute,
    } = orderFilterParams;

    const params: TypeGetOrdersParams = {
      offset: currentPage,
      limit: pageSize,
      orderCodes: keywordType?.value === KEYWORD_TYPE.OUTBOUND_ORDER_CODE ? keyword : '',
      ordererId: keywordType?.value === KEYWORD_TYPE.ORDERER_ID ? keyword : '',
      receiverName: keywordType?.value === KEYWORD_TYPE.RECEIVER_NAME ? keyword : '',
      clientOrderCodes: keywordType?.value === KEYWORD_TYPE.CLIENT_ORDER_CODE ? keyword : '',
      outboundStatus: outboundStatus.value,
      deliveryPolicy: deliveryPolicy.value,
      courierCode: courierCode.value,
      clusterCenter: clusterCenter.value,
      deliveryStartDate:
        periodType?.value === PERIOD_TYPE.DELIVERY_DATE && deliveryStartDate
          ? formatDate(deliveryStartDate, 'YYYY-MM-DD') ?? ''
          : '',
      deliveryEndDate:
        periodType?.value === PERIOD_TYPE.DELIVERY_DATE && deliveryEndDate
          ? formatDate(deliveryEndDate, 'YYYY-MM-DD') ?? ''
          : '',
      paymentStartAt:
        periodType?.value === PERIOD_TYPE.PAYMENT_AT && paymentStartAt
          ? `${formatDate(paymentStartAt, 'YYYY-MM-DD')} ${paymentStartAtTime}:${paymentStartAtMinute}:59`
          : '',
      paymentEndAt:
        periodType?.value === PERIOD_TYPE.PAYMENT_AT && paymentEndAt
          ? `${formatDate(paymentEndAt, 'YYYY-MM-DD')} ${paymentEndAtTime}:${paymentEndAtMinute}:59`
          : '',
    };

    Object.entries(params).forEach(([key, value]) => {
      if (value === '') {
        delete params[key as keyof TypeGetOrdersParams];
      }
    });

    requestOrderList(params, {
      onSuccess: ({ data, pagination }) => {
        setOrdersData(data);
        setTotalCount(pagination.totalCount);
        setOrderFilter(orderFilterParams);
        setSearchedParams(params);

        markDownloadReady({ filterValue: orderFilterParams, hasData: data.length > 0 });
      },
      onError: (error) => {
        const errorCode = error?.code;

        if (errorCode === ORDERS.INVALID_PAYMENT_AT_DATE_FORMAT || errorCode === ORDERS.CHECK_PAYMENT_AT_DATE) {
          setOrderFilterState({ ...orderFilterState, paymentAt: 'error' });
        }

        if (errorCode === ORDERS.INVALID_DATE_RANGE) {
          if (periodType?.value === PERIOD_TYPE.DELIVERY_DATE) {
            setOrderFilterState({ ...orderFilterState, deliveryDate: 'error' });
          } else if (periodType?.value === PERIOD_TYPE.PAYMENT_AT) {
            setOrderFilterState({ ...orderFilterState, paymentAt: 'error' });
          }
        }

        if (errorCode === ORDERS.INVALID_ORDER_CODES) {
          setOrderFilterState({ ...orderFilterState, keyword: 'error' });
        }

        dialogService.addDialog({
          children: error?.message || '주문 정보를 호출하는데 실패했습니다.\n다시 시도해주세요.',
          onConfirm: () => setOrdersData([]),
        });
      },
    });
  };

  const fetchOrderDefaultExcelDownload = async () => {
    const downloadingToastId = toastService.addToast({ message: TOAST_MESSAGE.DOWNLOADING, persist: true });

    try {
      await getOrderDefaultExcel();

      toastService.addToast({
        message: TOAST_MESSAGE.DOWNLOAD_SUCCESS,
      });
    } catch (error) {
      toastService.addToast({
        type: 'error',
        message: (error as TypeErrorResponseData['error'])?.message || TOAST_MESSAGE.DEFAULT_ERROR,
      });
    } finally {
      toastService.removeToast(downloadingToastId);
    }
  };

  const fetchOrderCancel = async (rows: IRowNode<TypeOrders>[]) => {
    const outboundOrderCodes = rows
      .map((row) => row.data?.outbound.outboundCode)
      .filter((code) => typeof code === 'string');

    try {
      setIsLoading(true);

      await postOrderCancel({ outboundOrderCodes });

      toastService.addToast({
        message: '출고 취소되었습니다.',
      });

      fetchOrdersData();
    } catch (error) {
      toastService.addToast({
        type: 'error',
        message: (error as TypeErrorResponseData['error'])?.message || TOAST_MESSAGE.DEFAULT_ERROR,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchOrdersOrderExcelDownload = async (reason: string) => {
    const downloadingToastId = toastService.addToast({ message: TOAST_MESSAGE.DOWNLOADING, persist: true });

    try {
      if (!searchedParams) {
        return;
      }

      const params = { ...searchedParams, downloadReason: reason };

      await getOrdersOrderExcel(params);

      toastService.addToast({
        message: TOAST_MESSAGE.DOWNLOAD_SUCCESS,
      });
    } catch (error) {
      toastService.addToast({
        type: 'error',
        message: (error as TypeErrorResponseData['error'])?.message || TOAST_MESSAGE.DEFAULT_ERROR,
      });
    } finally {
      toastService.removeToast(downloadingToastId);
    }
  };

  const fetchOrdersInvoiceNumberExcelDownload = async (reason: string) => {
    const downloadingToastId = toastService.addToast({ message: TOAST_MESSAGE.DOWNLOADING, persist: true });

    try {
      if (!searchedParams) {
        return;
      }

      const params = { ...searchedParams, downloadReason: reason };

      await getOrdersInvoiceNumberExcel(params);

      toastService.addToast({
        message: TOAST_MESSAGE.DOWNLOAD_SUCCESS,
      });
    } catch (error) {
      toastService.addToast({
        type: 'error',
        message: (error as TypeErrorResponseData['error'])?.message || TOAST_MESSAGE.DEFAULT_ERROR,
      });
    } finally {
      toastService.removeToast(downloadingToastId);
    }
  };

  const handleClickExcelTemplateDownload = () => {
    fetchOrderDefaultExcelDownload();
  };

  const handleClickOrderExcelUpload = () => {
    const currentHour = new Date().getHours();

    const convertToTimeString = (hour: number) => hour.toString().padStart(2, '0');

    if (currentHour >= ORDER_UPLOAD_RESTRICTED_HOUR_START || currentHour < ORDER_UPLOAD_RESTRICTED_HOUR_END) {
      dialogService.addDialog({
        type: 'alert',
        children: `${convertToTimeString(ORDER_UPLOAD_RESTRICTED_HOUR_START)}시~${convertToTimeString(
          ORDER_UPLOAD_RESTRICTED_HOUR_END
        )}시에는 주문을 등록할 수 없습니다.\n${convertToTimeString(
          ORDER_UPLOAD_RESTRICTED_HOUR_END
        )}시 이후 다시 시도해주세요.`,
        confirmText: '확인',
      });

      return;
    }

    handleOpenOrderExcelUploadModal();
  };

  const handleOrderExcelUploadResult = (data: TypePostOrderExcelResponse) => {
    if (data.totalCount === 0) {
      dialogService.addDialog({
        children: '입력된 데이터가 없습니다.',
      });

      return;
    } else if (data.failCount === 0) {
      toastService.addToast({
        message: '등록되었습니다.\n목록에 표시되지 않으면 새로고침 후 확인해 주세요.',
      });

      fetchOrdersData({
        ...orderFilter,
        currentPage: INITIAL_ORDER_PAGE_OFFSET,
      });
    } else {
      setExcelUploadFailData(data);

      handleOpenOrderExcelUploadFailModal();
    }
  };

  const handleChangeKeywordOption = (_: unknown, value: TypeSelectOption<string>) => {
    setOrderFilter({
      ...orderFilter,
      keywordType: value,
      keyword: '',
    });

    setOrderFilterState({ ...orderFilterState, keyword: 'default' });
  };

  const handleClickKeywordInput = () => {
    setOrderFilterState({ ...orderFilterState, keyword: 'default' });
  };

  const handleChangeKeywordInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderFilter({ ...orderFilter, keyword: e.target.value });
  };

  const handleSelectPeriodOption = (_: unknown, type: TypeSelectOption<string>) => {
    setOrderFilter({
      ...orderFilter,
      periodType: type,
      ...(orderFilter.periodType?.value === PERIOD_TYPE.DELIVERY_DATE && {
        deliveryStartDate: getDateByOffset(1, 'day'),
        deliveryEndDate: getDateByOffset(1, 'day'),
      }),
      ...(orderFilter.periodType?.value === PERIOD_TYPE.PAYMENT_AT && {
        paymentStartAt: null,
        paymentStartAtTime: '',
        paymentStartAtMinute: '',
        paymentEndAt: null,
        paymentEndAtTime: '',
        paymentEndAtMinute: '',
      }),
    });

    setOrderFilterState({
      ...orderFilterState,
      ...(orderFilter.periodType?.value === PERIOD_TYPE.DELIVERY_DATE && {
        deliveryDate: 'default',
      }),
      ...(orderFilter.periodType?.value === PERIOD_TYPE.PAYMENT_AT && {
        paymentAt: 'default',
      }),
    });
  };

  const handleChangeDeliveryDateRange = (selectedDate: { start: Date | null; end: Date | null } | null) => {
    const startDate = selectedDate?.start ?? null;
    const endDate = selectedDate?.end ?? null;

    setOrderFilter({
      ...orderFilter,
      deliveryStartDate: startDate,
      deliveryEndDate: endDate,
    });
  };

  const handleFocusDeliveryDateRange = () => {
    if (orderFilterState.deliveryDate === 'error') {
      setOrderFilterState({ ...orderFilterState, deliveryDate: 'default' });
    }
  };

  const handleClickAnyPaymentAtInput = () => {
    if (orderFilterState.paymentAt === 'error') {
      setOrderFilterState({ ...orderFilterState, paymentAt: 'default' });
    }
  };

  const handleChangePaymentStartAtDatePicker = (date: Date | null) => {
    setOrderFilter({
      ...orderFilter,
      paymentStartAt: date,
      paymentStartAtTime: '00',
      paymentStartAtMinute: '00',
    });
  };

  const handleChangePaymentStartAtTimeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderFilter({ ...orderFilter, paymentStartAtTime: e.target.value });
  };

  const handleBlurPaymentStartAtTimeInput = () => {
    setOrderFilter({
      ...orderFilter,
      paymentStartAtTime: formatTime(orderFilter.paymentStartAtTime),
    });
  };

  const handleChangePaymentStartAtMinuteInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderFilter({ ...orderFilter, paymentStartAtMinute: e.target.value });
  };

  const handleBlurPaymentStartAtMinuteInput = () => {
    setOrderFilter({
      ...orderFilter,
      paymentStartAtMinute: formatMinute(orderFilter.paymentStartAtMinute),
    });
  };

  const handleChangePaymentEndAtDatePicker = (date: Date | null) => {
    setOrderFilter({
      ...orderFilter,
      paymentEndAt: date,
      paymentEndAtTime: '23',
      paymentEndAtMinute: '59',
    });
  };

  const handleInputClickPaymentEndAtDatePicker = () => {
    setOrderFilterState({ ...orderFilterState, paymentAt: 'default' });
  };

  const handleChangePaymentEndAtTimeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderFilter({ ...orderFilter, paymentEndAtTime: e.target.value });
  };

  const handleBlurPaymentEndAtTimeInput = () => {
    setOrderFilter({
      ...orderFilter,
      paymentEndAtTime: formatTime(orderFilter.paymentEndAtTime),
    });
  };

  const handleChangePaymentEndAtMinuteInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderFilter({ ...orderFilter, paymentEndAtMinute: e.target.value });
  };

  const handleBlurPaymentEndAtMinuteInput = () => {
    setOrderFilter({
      ...orderFilter,
      paymentEndAtMinute: formatMinute(orderFilter.paymentEndAtMinute),
    });
  };

  const handleChangeSelect = (
    e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
    option: TypeSelectOption
  ) => {
    setOrderFilter({ ...orderFilter, [e.currentTarget.id]: option });
  };

  const handleClickReset = () => {
    setOrderFilter({
      ...INITIAL_ORDER_FILTER,
      keywordType: optionKeywordType[0] ?? null,
      periodType: optionPeriodType[0] ?? null,
    });

    setOrderFilterState({
      keyword: 'default',
      deliveryDate: 'default',
      paymentAt: 'default',
    });
  };

  const handleChangePagesizeSelect = (_: unknown, { value }: TypeSelectOption<TypeOrderPageSize>) => {
    fetchOrdersData({
      ...orderFilter,
      pageSize: value,
      currentPage: INITIAL_ORDER_PAGE_OFFSET,
    });
  };

  const handleClickPageButton = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    fetchOrdersData({ ...orderFilter, currentPage: Number(e.currentTarget.value) - 1 });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const {
      keywordType,
      keyword,
      paymentStartAt,
      paymentStartAtTime,
      paymentStartAtMinute,
      paymentEndAt,
      paymentEndAtTime,
      paymentEndAtMinute,
    } = orderFilter;

    /** '출고요청번호'에 알파벳, 숫자 외 다른 문자가 포함된 경우 제한 */
    if (keywordType?.value === KEYWORD_TYPE.OUTBOUND_ORDER_CODE && keyword) {
      const keywordWithOutComma = keyword.replaceAll(',', '');

      if (!validateAlphabetNumericOnly(keywordWithOutComma)) {
        dialogService.addDialog({
          children: '올바른 형식의 출고요청번호가 아닙니다.\n확인 후 다시 검색해주세요.',
        });
        setOrderFilterState({ ...orderFilterState, keyword: 'error' });
        return;
      }
    }

    /** '출고요청번호' 복수 입력 시, 최대 10개 검색 제한 */
    if (keywordType?.value === KEYWORD_TYPE.OUTBOUND_ORDER_CODE) {
      if (keyword.split(',').length > MAX_KEYWORD_COUNT) {
        dialogService.addDialog({
          type: 'alert',
          children: <span>출고요청번호는 최대 {MAX_KEYWORD_COUNT}개까지 검색할 수 있습니다.</span>,
          confirmText: '확인',
          onConfirm: () => setOrdersData([]),
        });

        setOrderFilterState({ ...orderFilterState, keyword: 'error' });

        return;
      }
    }

    /** '판매처 주문번호' 복수 입력 시, 최대 10개 검색 제한 */
    if (keywordType?.value === KEYWORD_TYPE.CLIENT_ORDER_CODE) {
      if (keyword.split(',').length > MAX_KEYWORD_COUNT) {
        dialogService.addDialog({
          type: 'alert',
          children: <span>판매처 주문번호는 최대 {MAX_KEYWORD_COUNT}개까지 검색할 수 있습니다.</span>,
          confirmText: '확인',
          onConfirm: () => setOrdersData([]),
        });

        setOrderFilterState({ ...orderFilterState, keyword: 'error' });

        return;
      }
    }

    /** '결제완료일시' 시작일이 종료일보다 늦은 경우 제한 */
    if (paymentStartAt && paymentEndAt) {
      const isEndDateFasterThanStartDate = paymentStartAt > paymentEndAt;
      const isEndTimeFasterThanStartTime =
        paymentStartAt.getTime() === paymentEndAt.getTime() && +paymentStartAtTime > +paymentEndAtTime;
      const isEndMinuteFasterThanStartMinute =
        paymentStartAt.getTime() === paymentEndAt.getTime() &&
        +paymentStartAtTime <= +paymentEndAtTime &&
        +paymentStartAtMinute > +paymentEndAtMinute;

      if (isEndDateFasterThanStartDate || isEndTimeFasterThanStartTime || isEndMinuteFasterThanStartMinute) {
        dialogService.addDialog({
          type: 'alert',
          children: <span>정확한 시간을 입력해주세요.</span>,
          confirmText: '확인',
          onConfirm: () => setOrdersData([]),
        });

        setOrderFilterState({ ...orderFilterState, paymentAt: 'error' });

        return;
      }
    }

    fetchOrdersData({
      ...orderFilter,
      currentPage: INITIAL_ORDER_PAGE_OFFSET,
    });
  };

  const handleChangeRowSelection = (selectedRows: IRowNode[]) => {
    setSelectedRows(selectedRows);
  };

  const handleClickOrderCancel = () => {
    const areAllRowsInStatus = (rows: IRowNode<TypeOrders>[], status: string) =>
      rows.every((row) => row.data?.outbound.outboundStatus.code === status);

    const isAnyRowInStatus = (rows: IRowNode<TypeOrders>[], status: string) =>
      rows.some((row) => row.data?.outbound.outboundStatus.code === status);

    const isAllReady = areAllRowsInStatus(selectedRows, OUTBOUND_STATUS.READY.code);
    const isAnyReady = isAnyRowInStatus(selectedRows, OUTBOUND_STATUS.READY.code);
    const isAllCanceled = areAllRowsInStatus(selectedRows, OUTBOUND_STATUS.CANCELED.code);

    if (isAllReady) {
      dialogService.addDialog({
        type: 'confirm',
        children: '선택한 주문번호에 대한 출고를\n취소하시겠습니까?',
        onConfirm: () => {
          fetchOrderCancel(selectedRows);
        },
      });
    } else if (isAnyReady) {
      dialogService.addDialog({
        type: 'confirm',
        children: (
          <>
            <Text element="span" size="large" weight="semi-bold">
              [대기중]{' '}
            </Text>
            <Text element="span" size="large">
              {'상태에서만 출고 취소가 가능합니다. 나머지 선택 항목만 출고 취소하시겠습니까?'}
            </Text>
            <Text element="span" size="medium" weight="semi-bold" color="tertiary">
              [진행중]{' '}
            </Text>
            <Text element="span" size="medium" color="tertiary">
              {'상태의 요청을 취소하고자 하는 경우\n컬리에 별도 문의해 주세요.'}
            </Text>
          </>
        ),
        onConfirm: () => {
          const readyStatusRows = selectedRows.filter(
            (row) => row.data?.outbound.outboundStatus.code === OUTBOUND_STATUS.READY.code
          );

          fetchOrderCancel(readyStatusRows);
        },
      });
    } else if (isAllCanceled) {
      dialogService.addDialog({
        children: (
          <>
            <Text element="span" size="large">
              이미 출고{' '}
            </Text>
            <Text element="span" size="large" weight="semi-bold">
              [취소]{' '}
            </Text>
            <Text element="span" size="large">
              상태입니다.
            </Text>
          </>
        ),
      });
    } else {
      dialogService.addDialog({
        children: (
          <>
            <Text element="span" size="large" weight="semi-bold">
              [대기중]{' '}
            </Text>
            <Text element="span" size="large">
              상태에서만 출고 취소가 가능합니다.
            </Text>
          </>
        ),
      });
    }
  };

  return (
    <PageWrapper>
      <PageTitle
        title="출고 주문 조회"
        breadcrumbs={BREAD_CRUMB[PATH.ORDER_ORDERS]}
        handleFilterToggle={handleFilterToggle}
      />
      {isFilterOpen && (
        <Form className={styles['filter-section']} onSubmit={handleSubmit}>
          <div className={styles['filters-container']}>
            <div className={`${styles['filter-container']} ${styles['filter-keyword']}`}>
              <Text className={styles['filter-label']} weight="semi-bold" size="small">
                키워드
              </Text>
              <div className={styles['input-container']}>
                <Select
                  className={styles['input-select']}
                  value={orderFilter.keywordType}
                  options={optionKeywordType}
                  size="small"
                  onChange={handleChangeKeywordOption}
                />
                <Input
                  type="text"
                  status={orderFilterState.keyword}
                  placeholder={KEYWORD_PLACE_HOLDER[orderFilter.keywordType?.value ?? '']}
                  value={orderFilter.keyword}
                  size="small"
                  maxLength={200}
                  onClick={handleClickKeywordInput}
                  onChange={handleChangeKeywordInput}
                />
              </div>
            </div>
            <div
              className={`
                ${orderFilter.periodType?.value === PERIOD_TYPE.PAYMENT_AT ? styles['filter-container-payment-at'] : ''}
                ${styles['filter-container']}
                ${styles['filter-period']}
              `}
            >
              <Text className={styles['filter-label']} weight="semi-bold" size="small">
                기간
              </Text>
              <div className={styles['input-container']}>
                <Select
                  className={styles['input-select']}
                  value={orderFilter.periodType}
                  options={optionPeriodType}
                  size="small"
                  onChange={handleSelectPeriodOption}
                />
                {orderFilter.periodType?.value === PERIOD_TYPE.DELIVERY_DATE && (
                  <DateRangePicker
                    size="small"
                    placeholder={{ start: '시작일', end: '종료일' }}
                    status={orderFilterState.deliveryDate}
                    value={getDateRangeInfo(orderFilter.deliveryStartDate, orderFilter.deliveryEndDate)}
                    onChange={handleChangeDeliveryDateRange}
                    onFocus={handleFocusDeliveryDateRange}
                  />
                )}
                {orderFilter.periodType?.value === PERIOD_TYPE.PAYMENT_AT && (
                  <div className={styles['date-picker-with-time-input']}>
                    <DatePicker
                      className={styles['date-picker-input']}
                      value={orderFilter.paymentStartAt}
                      status={orderFilterState.paymentAt}
                      placeholder="날짜 선택"
                      size="small"
                      onChange={handleChangePaymentStartAtDatePicker}
                      onFocus={handleClickAnyPaymentAtInput}
                    />
                    <Input
                      className={styles['input-time']}
                      value={orderFilter.paymentStartAtTime}
                      status={orderFilterState.paymentAt}
                      type="number"
                      placeholder="시"
                      size="small"
                      min={0}
                      max={24}
                      onClick={handleClickAnyPaymentAtInput}
                      onChange={handleChangePaymentStartAtTimeInput}
                      onBlur={handleBlurPaymentStartAtTimeInput}
                    />
                    {':'}
                    <Input
                      className={styles['input-time']}
                      value={orderFilter.paymentStartAtMinute}
                      status={orderFilterState.paymentAt}
                      type="number"
                      placeholder="분"
                      size="small"
                      maxLength={2}
                      min={0}
                      max={59}
                      onClick={handleClickAnyPaymentAtInput}
                      onChange={handleChangePaymentStartAtMinuteInput}
                      onBlur={handleBlurPaymentStartAtMinuteInput}
                    />
                    {'-'}
                    <DatePicker
                      className={styles['date-picker-input']}
                      value={orderFilter.paymentEndAt}
                      status={orderFilterState.paymentAt}
                      placeholder="날짜 선택"
                      size="small"
                      onChange={handleChangePaymentEndAtDatePicker}
                      onFocus={handleInputClickPaymentEndAtDatePicker}
                      ref={paymentEndAtDateRef}
                    />
                    <Input
                      className={styles['input-time']}
                      value={orderFilter.paymentEndAtTime}
                      status={orderFilterState.paymentAt}
                      type="number"
                      placeholder="시"
                      size="small"
                      min={0}
                      max={24}
                      onClick={handleClickAnyPaymentAtInput}
                      onChange={handleChangePaymentEndAtTimeInput}
                      onBlur={handleBlurPaymentEndAtTimeInput}
                    />
                    {':'}
                    <Input
                      className={styles['input-time']}
                      value={orderFilter.paymentEndAtMinute}
                      status={orderFilterState.paymentAt}
                      type="number"
                      placeholder="분"
                      size="small"
                      min={0}
                      max={59}
                      onClick={handleClickAnyPaymentAtInput}
                      onChange={handleChangePaymentEndAtMinuteInput}
                      onBlur={handleBlurPaymentEndAtMinuteInput}
                    />
                  </div>
                )}
              </div>
            </div>
            <div className={`${styles['filter-column-container']} ${styles['filter-status']}`}>
              <div className={styles['filter-container']}>
                <Text className={styles['filter-label']} weight="semi-bold" size="small">
                  출고상태
                </Text>
                <Select
                  id="outboundStatus"
                  value={orderFilter.outboundStatus}
                  options={optionOutboundStatus}
                  size="small"
                  onChange={handleChangeSelect}
                />
              </div>
              <div className={styles['filter-container']}>
                <Text className={styles['filter-label']} weight="semi-bold" size="small">
                  배송 유형
                </Text>
                <Select
                  id="deliveryPolicy"
                  value={orderFilter.deliveryPolicy}
                  options={optionDeliveryPolicy}
                  size="small"
                  onChange={handleChangeSelect}
                />
              </div>
            </div>
            <div className={`${styles['filter-column-container']} ${styles['filter-courier-type']}`}>
              <div className={styles['filter-container']}>
                <Text className={styles['filter-label']} weight="semi-bold" size="small">
                  배송사
                </Text>
                <Select
                  id="courierCode"
                  value={orderFilter.courierCode}
                  options={optionCourierCode}
                  size="small"
                  onChange={handleChangeSelect}
                />
              </div>
              <div className={styles['filter-container']}>
                <Text className={styles['filter-label']} weight="semi-bold" size="small">
                  생산센터
                </Text>
                <Select
                  id="clusterCenter"
                  value={orderFilter.clusterCenter}
                  options={optionClusterCenter}
                  size="small"
                  onChange={handleChangeSelect}
                />
              </div>
            </div>
          </div>
          <div className={styles['filter-actions']}>
            <div className={styles['filter-actions-left-field']} />
            <div className={styles['filter-actions-center-field']}>
              <Button type="submit" size="small">
                검색
              </Button>
            </div>
            <div className={styles['filter-actions-right-field']}>
              <TextButton
                className={styles['filter-reset-button']}
                leftIcon={<RefreshIcon size={16} />}
                size="small"
                color="secondary"
                onClick={handleClickReset}
              >
                필터 초기화
              </TextButton>
            </div>
          </div>
        </Form>
      )}
      <div className={styles['view-section']}>
        <TableTopField
          dataTotalCount={totalCount ?? 0}
          rightButton={
            (hasPermission3PL || hasPermissionNFAFulfillment) && (
              <div className={styles['view-section-top-right-field']}>
                {hasPermission3PL && (
                  <>
                    <Button
                      variant="outlined"
                      status="error"
                      size="tiny"
                      disabled={selectedRows.length === 0}
                      onClick={handleClickOrderCancel}
                    >
                      출고 취소
                    </Button>
                    <Divider className={styles['divider']} color="gray100" orientation="vertical" />
                  </>
                )}
                <Button
                  variant="outlined"
                  color="tertiary"
                  size="tiny"
                  leftIcon={<ExcelIcon size={16} />}
                  disabled={!isDownloadReady}
                  onClick={handleOpenOrderExcelDownloadModal}
                >
                  주문 엑셀 다운로드
                </Button>
                <Button
                  variant="outlined"
                  color="tertiary"
                  size="tiny"
                  leftIcon={<ExcelIcon size={16} />}
                  disabled={!isDownloadReady}
                  onClick={handleOpenInvoiceExcelDownloadModal}
                >
                  송장 엑셀 다운로드
                </Button>
                {hasPermission3PL && (
                  <>
                    <Divider className={styles['divider']} color="gray100" orientation="vertical" />
                    <Button variant="outlined" color="tertiary" size="tiny" onClick={handleClickExcelTemplateDownload}>
                      엑셀 양식 다운로드
                    </Button>
                    <Button variant="outlined" color="tertiary" size="tiny" onClick={handleClickOrderExcelUpload}>
                      주문 엑셀 일괄 등록
                    </Button>
                  </>
                )}
              </div>
            )
          }
        />
        <div className={styles['view-section-table-wrapper']}>
          <Table
            className={styles['view-table']}
            data={ordersData}
            options={{ columnDefs }}
            rowStatusType={ordersData === null ? 'INIT' : 'EMPTY'}
            onSelectionChange={handleChangeRowSelection}
          />
        </div>
      </div>
      <div className={styles['view-actions-section']}>
        <div className={styles['view-options']}>
          <Select
            className={styles['limit-select']}
            options={pageOptions}
            value={ORDER_PAGE_SIZE[orderFilter.pageSize]}
            size="tiny"
            dropdownPlacement="top-left"
            onChange={handleChangePagesizeSelect}
          />
        </div>
        {totalCount ? (
          <Pagination
            className={styles['pagination']}
            currentPage={orderFilter.currentPage + 1}
            totalCount={totalCount ?? 0}
            pageSize={orderFilter.pageSize}
            onClick={handleClickPageButton}
          />
        ) : null}
      </div>
      {isOrderDetailModalOpen && (
        <OrderDetailModal
          open={isOrderDetailModalOpen}
          data={orderDetailsData}
          onConfirm={handleCloseOrderDetailModal}
        />
      )}
      {isOrderExcelUploadModalOpen && (
        <OrderExcelUploadModal
          open={isOrderExcelUploadModalOpen}
          onCancel={handleCancelOrderExcelUploadModal}
          onConfirm={handleConfirmOrderExcelUploadModal}
        />
      )}
      {isOrderExcelUploadFailModalOpen && excelUploadFailData && (
        <OrderExcelUploadFailModal
          failData={excelUploadFailData}
          open={isOrderExcelUploadFailModalOpen}
          onClose={handleCloseOrderExcelUploadFailModal}
        />
      )}
      {isOrderExcelDownloadModalOpen && (
        <OrderExcelDownloadModal
          open={isOrderExcelDownloadModalOpen}
          onCancel={handleCloseOrderExcelDownloadModal}
          onConfirm={handleConfirmOrderExcelDownloadModal}
        />
      )}
      {isInvoiceExcelDownloadModalOpen && (
        <OrderExcelDownloadModal
          open={isInvoiceExcelDownloadModalOpen}
          onCancel={handleCloseInvoiceExcelDownloadModal}
          onConfirm={handleConfirmInvoiceExcelDownloadModal}
        />
      )}
    </PageWrapper>
  );
};

export default OrderOrdersPage;
